/**
 * Servicio de caché usando sessionStorage
 * Los datos se limpian automáticamente cuando se cierra la pestaña
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

class CacheService {
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  private getKey(key: string): string {
    return `cache_${key}`;
  }

  /**
   * Guarda datos en el caché
   */
  set<T>(key: string, data: T): void {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(this.getKey(key), JSON.stringify(item));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  }

  /**
   * Obtiene datos del caché
   * Retorna null si no existe o está expirado
   */
  get<T>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(this.getKey(key));
      if (!item) return null;

      const cached: CacheItem<T> = JSON.parse(item);
      const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;

      if (isExpired) {
        this.remove(key);
        return null;
      }

      return cached.data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  /**
   * Elimina un item específico del caché
   */
  remove(key: string): void {
    try {
      sessionStorage.removeItem(this.getKey(key));
    } catch (error) {
      console.error('Error removing from cache:', error);
    }
  }

  /**
   * Limpia todos los items del caché
   */
  clear(): void {
    try {
      // Limpiar solo los items de cache
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('cache_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
}

export const cacheService = new CacheService();
